import { useState } from 'react'
import { CheckIcon } from '@heroicons/react/24/solid'
import { PLANS, type Plan } from '../content/pricing'
import { RegisterModal } from '@components/registration/RegisterModal'

function PlanCard({ plan, onGetStarted }: { plan: Plan; onGetStarted: () => void }) {
  const highlighted = plan.highlighted

  return (
    <div
      className={`relative flex flex-col rounded-2xl border p-6 ${
        highlighted
          ? 'border-dash-accent/60 bg-dash-surface shadow-lg shadow-dash-accent/10'
          : 'border-dash-border bg-dash-surface'
      }`}
    >
      {plan.badge && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-gradient-to-r from-dash-accent to-dash-accent-2 px-3 py-1 text-xs font-semibold text-white">
          {plan.badge}
        </span>
      )}

      <h3 className="text-lg font-semibold text-dash-text">{plan.name}</h3>

      <div className="mt-3 flex items-baseline gap-1">
        <span className="font-mono text-4xl font-bold tracking-tight text-dash-text tabular-nums">
          {plan.price}
        </span>
        {plan.period && <span className="text-sm text-dash-secondary">{plan.period}</span>}
      </div>

      <p className="mt-3 text-sm leading-relaxed text-dash-secondary">{plan.tagline}</p>

      <ul className="mt-6 flex-1 space-y-3">
        {plan.features.map((feature) => (
          <li key={feature} className="flex items-start gap-2.5 text-sm text-dash-secondary">
            <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-dash-accent" aria-hidden="true" />
            <span>{feature}</span>
          </li>
        ))}
      </ul>

      <button
        type="button"
        onClick={onGetStarted}
        className={`mt-8 inline-flex w-full items-center justify-center rounded-lg px-4 py-2.5 text-sm font-semibold transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-dash-surface ${
          highlighted
            ? 'bg-gradient-to-r from-dash-accent to-dash-accent-2 text-white hover:opacity-90 focus-visible:ring-dash-accent'
            : 'border border-dash-border text-dash-text transition-colors hover:bg-dash-bg focus-visible:ring-dash-accent'
        }`}
      >
        {plan.cta}
      </button>
    </div>
  )
}

export function Pricing() {
  const [showRegister, setShowRegister] = useState(false)

  return (
    <section id="pricing" className="scroll-mt-20 bg-dash-bg">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-28">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-3xl font-bold tracking-tight text-dash-text sm:text-4xl">
            Simple, plan-based pricing
          </h2>
          <p className="mt-4 text-lg text-dash-secondary">
            Pick a plan that fits your volume. All plans include every sport and the full matching
            pipeline.
          </p>
        </div>

        <div className="mx-auto mt-14 grid max-w-5xl gap-6 md:grid-cols-3">
          {PLANS.map((plan) => (
            <PlanCard key={plan.id} plan={plan} onGetStarted={() => setShowRegister(true)} />
          ))}
        </div>

        <p className="mx-auto mt-10 max-w-2xl text-center text-sm text-dash-muted">
          Keys are activated by our team after registration &mdash; no self-serve billing yet.
        </p>
      </div>

      {showRegister && <RegisterModal onClose={() => setShowRegister(false)} redirectTo="/admin" />}
    </section>
  )
}
